import { StudyConfig } from '@csp/csp-common-config';
import {
  BioSampleContainerQueryService,
  BioSampleQueryMapper,
  BioSampleQueryService,
  BioSampleService,
} from '@csp/csp-common-hbs';
import { BioSample, BioSampleContainer, SampleSortOrderType } from '@csp/csp-common-hbs-model';
import { PagedResultMapper } from '@csp/csp-common-mapper';
import {
  ApiOptions,
  Author,
  CspError,
  Maybe,
  OrgCriteria,
  PagedResult,
  Patient,
  Study,
  UserId,
  VisitId,
} from '@csp/csp-common-model';
import { CachedPatientQueryService, PatientQueryMapper, PatientQueryService } from '@csp/csp-common-patient';
import { AuthorQueryService } from '@csp/csp-common-user-query';
import { isDefined } from '@csp/csp-common-util';
import { toRestOptions } from '@csp/csp-fe-auth';
import { Page } from '@csp/dmdp-api-common-dto';
import { UserIncludeFieldType } from '@csp/dmdp-api-user-dto';
import uniq from 'lodash/uniq';
import { BookedPatientVisit } from '../../patient-visits/model/BookedPatientVisit';
import { BioSampleItemMapper } from '../mapper/BioSampleItemMapper';
import { BioSampleItem } from '../model/BioSampleItem';
import { BioSamplesItemPage } from '../model/BioSamplesItemPage';
import { FetchBioSampleItemBySampleCodeIn } from '../model/FetchBioSampleItemBySampleCodeIn';
import { FetchBioSampleItemBySampleIdIn } from '../model/FetchBioSampleItemBySampleIdIn';

import { CachedBookedPatientVisitQueryService } from '../../patient-list/patient-list-visit/service/CachedBookedPatientVisitQueryService';
import { QueryBioSamplesItemsIn } from '../model/QueryBioSamplesItemsIn';

type BookedVisitsAndPatients = {
  bookedVisits: BookedPatientVisit[];
  patients: Patient[];
};

const queryCachedPatientsByBioSamples = async (
  bioSamples: BioSample[],
  study: Study,
  studyConfig: StudyConfig,
  options: ApiOptions,
): Promise<Patient[]> => {
  const userIds = bioSamples.flatMap(bioSample => bioSample.patientId);

  const uniqueUserIds = uniq(userIds);

  if (uniqueUserIds.length) {
    return await CachedPatientQueryService.getPatientsByUserIds(
      uniqueUserIds,
      PatientQueryMapper.defaultFields,
      studyConfig,
      study.sites,
      options,
    );
  } else {
    return [];
  }
};

const queryVisitsByBioSamples = async (
  bioSamples: BioSample[],
  study: Study,
  studyConfig: StudyConfig,
  apiOptions: ApiOptions,
): Promise<BookedPatientVisit[]> => {
  const visitIds: VisitId[] = bioSamples.flatMap(bioSample => (bioSample.visitId ? bioSample.visitId : ''));

  const uniqueVisitIds = uniq(visitIds);

  if (uniqueVisitIds.length) {
    const bookedVisits = await CachedBookedPatientVisitQueryService.fetchByIds(
      uniqueVisitIds,
      study,
      studyConfig,
      apiOptions,
    );

    return bookedVisits;
  } else {
    return [];
  }
};

const queryPatientIdsBySiteIds = async (org: OrgCriteria, apiOptions: ApiOptions): Promise<UserId[]> => {
  const patientsPage = await PatientQueryService.query(
    {
      pageSize: Page.maxLimit,
      criteria: { org },
      fields: [UserIncludeFieldType.USER_ID],
      key: 'patient-ids-by-site-ids',
    },
    undefined,
    undefined,
    undefined,
    apiOptions,
  );

  return patientsPage.data?.map(patient => patient.userId);
};

const evaluateSiteIdsResults = (
  org: Maybe<OrgCriteria>,
  patientIds: Maybe<UserId[]>,
): { hasNoResults: boolean; hasTooManyResults: boolean } => {
  const hasOrgIdsCriteria = Boolean(org?.siteIds?.length);
  const hasNoResults = hasOrgIdsCriteria && patientIds?.length === 0;
  const hasTooManyResults = hasOrgIdsCriteria && patientIds ? patientIds.length >= Page.maxLimit : false;

  return { hasNoResults, hasTooManyResults };
};

const toBioSampleItem = (
  bioSample: BioSample,
  bioSampleContainer: Maybe<BioSampleContainer>,
  aliquotParent: Maybe<BioSampleItem>,
  aliquotChildren: Maybe<BioSampleItem[]>,
  bookedVisits: BookedPatientVisit[],
  patients: Patient[],
  authors: Author[],
): Maybe<BioSampleItem> => {
  const bookedVisit = bookedVisits.find(visit => visit.visitId === bioSample.visitId);
  const patient = patients.find(patient => patient.userId === bioSample.patientId);
  if (patient) {
    return BioSampleItemMapper.toBioSampleItem(
      bioSample,
      bioSampleContainer,
      aliquotParent,
      aliquotChildren,
      patient,
      bookedVisit,
      authors,
    );
  } else {
    console.warn(`No patient found for bioSample with ID ${bioSample.sampleId}`);
    return undefined;
  }
};

const toBioSampleItems = (
  bioSamples: BioSample[],
  bioSampleContainers: BioSampleContainer[],
  bookedVisits: BookedPatientVisit[],
  patients: Patient[],
  authors: Author[],
): BioSampleItem[] =>
  bioSamples
    .map(bioSample =>
      toBioSampleItem(
        bioSample,
        bioSampleContainers.find(
          container =>
            container.kitCode === bioSample.kitCode && container.containerNumber === bioSample.containerNumber,
        ),
        undefined,
        undefined,
        bookedVisits,
        patients,
        authors,
      ),
    )
    .filter(isDefined);

const queryVisitsAndPatientsByBioSamples = async (
  bioSamples: BioSample[],
  study: Study,
  studyConfig: StudyConfig,
  apiOptions: ApiOptions,
): Promise<{ bookedVisits: BookedPatientVisit[]; patients: Patient[] }> => {
  const [bookedVisits, patients] = await Promise.all([
    await queryVisitsByBioSamples(bioSamples, study, studyConfig, apiOptions),
    await queryCachedPatientsByBioSamples(bioSamples, study, studyConfig, apiOptions),
  ]);

  return {
    bookedVisits,
    patients,
  };
};

const fetchAuthorsForSample = async (bioSamples: BioSample[], apiOptions?: ApiOptions): Promise<Author[]> => {
  const authorUserIds: UserId[] = uniq(
    bioSamples.flatMap(bioSample => [
      bioSample.createdBy,
      bioSample.collectionStatus.createdUserId,
      bioSample.processingStatus.evaluatedStatusValue.createdUserId,
      bioSample.shippingStatus.createdUserId,
      bioSample.processComplianceStatus.createdUserId,
      bioSample.storageComplianceStatus.createdUserId,
      bioSample.analyteType.createdUserId,
      bioSample.processedSampleType.createdUserId,
      bioSample.deliveryLab.createdUserId,
      ...bioSample.commentStatusValues.flatMap(commentStatusValue => commentStatusValue.createdUserId),
    ]),
  ).filter(isDefined);

  return await AuthorQueryService.byUserIds(authorUserIds, apiOptions);
};

const queryEnrichmentData = async (
  bioSamples: BioSample[],
  study: Study,
  studyConfig: StudyConfig,
  includeAuthors: boolean,
  includeBioSampleContainer: boolean,
  apiOptions: ApiOptions,
): Promise<{
  bookedVisitsAndPatients: BookedVisitsAndPatients;
  authors: Author[];
  bioSampleContainers: BioSampleContainer[];
}> => {
  const [bookedVisitsAndPatients, authors, bioSampleContainers] = await Promise.all([
    queryVisitsAndPatientsByBioSamples(bioSamples, study, studyConfig, apiOptions),
    includeAuthors ? fetchAuthorsForSample(bioSamples, apiOptions) : [],
    includeBioSampleContainer
      ? BioSampleContainerQueryService.queryCachedByRefs(
          bioSamples.map(bioSample => ({
            kitCode: bioSample.kitCode,
            containerNumber: bioSample.containerNumber ?? '',
          })),
        )
      : [],
  ]);

  return {
    bookedVisitsAndPatients,
    authors,
    bioSampleContainers,
  };
};

const query = async (
  {
    bioSampleQueryIn,
    studyConfig,
    study,
    next,
    includeBioSampleContainer = false,
    includeAuthors = false,
  }: QueryBioSamplesItemsIn,
  apiOptions?: ApiOptions,
): Promise<BioSamplesItemPage> => {
  const restOptions = toRestOptions(apiOptions);
  const patientIds = bioSampleQueryIn.criteria?.org?.siteIds?.length
    ? await queryPatientIdsBySiteIds(bioSampleQueryIn.criteria.org, restOptions)
    : undefined;

  const queryIn = { ...bioSampleQueryIn };

  if (isDefined(patientIds)) {
    queryIn.criteria = {
      ...queryIn.criteria,
      orgIdPatientIds: patientIds,
    };
  }

  const isContinueQuery = !patientIds || patientIds.length; // If there were no patients found => the query should stop

  const bioSamples: PagedResult<BioSample> = isContinueQuery
    ? await BioSampleQueryService.query(queryIn, next, restOptions)
    : {
        data: [],
      };

  const {
    bookedVisitsAndPatients: { bookedVisits, patients },
    authors,
    bioSampleContainers,
  } = await queryEnrichmentData(
    bioSamples.data,
    study,
    studyConfig,
    includeAuthors,
    includeBioSampleContainer,
    restOptions,
  );

  const bioSamplesItems = toBioSampleItems(bioSamples.data, bioSampleContainers, bookedVisits, patients, authors);

  const siteIdsResultsEvaluation = evaluateSiteIdsResults(bioSampleQueryIn.criteria?.org, patientIds);

  return {
    tooLargeQuery: siteIdsResultsEvaluation.hasTooManyResults,
    hasBioSamples: (bioSamples.data.length ?? bioSamples?.count ?? 0) > 0,
    ...PagedResultMapper.toPagedResult(bioSamples, bioSamplesItems),
  };
};

const fetchAliquots = async (
  {
    bioSample,
    study,
    studyConfig,
    includeAliquotParent = false,
    includeAliquotChildren = false,
  }: {
    bioSample: BioSample;
    study: Study;
    studyConfig: StudyConfig;
    includeAliquotParent?: boolean;
    includeAliquotChildren?: boolean;
  },
  restOptions?: ApiOptions,
): Promise<{
  aliquotParent: Maybe<BioSampleItem>;
  aliquotChildren: BioSampleItem[];
}> => {
  const aliquotParent: Maybe<BioSampleItem> =
    includeAliquotParent && bioSample.aliquotParentSampleId
      ? await fetchById({ sampleId: bioSample.aliquotParentSampleId, study, studyConfig }, restOptions)
      : undefined;

  const aliquotChildren: BioSampleItem[] =
    includeAliquotChildren && bioSample.isAliquotParent
      ? (
          await query({
            study,
            studyConfig,
            bioSampleQueryIn: {
              pageSize: Page.maxLimit,
              criteria: { aliquotParentIds: [bioSample.sampleId] },
              sort: SampleSortOrderType.SAMPLE_CODE,
            },
          })
        ).data
      : [];

  return {
    aliquotParent,
    aliquotChildren,
  };
};

const fetchById = async (
  {
    sampleId,
    study,
    studyConfig,
    includeAuthors,
    includeAliquotParent,
    includeAliquotChildren,
    includeBioSampleContainer,
  }: FetchBioSampleItemBySampleIdIn,
  apiOptions?: ApiOptions,
): Promise<BioSampleItem> => {
  const restOptions = toRestOptions(apiOptions);
  const bioSample = await BioSampleService.fetchById(sampleId, apiOptions);

  const {
    bookedVisitsAndPatients: { bookedVisits, patients },
    authors,
    bioSampleContainers,
  } = await queryEnrichmentData(
    [bioSample],
    study,
    studyConfig,
    !!includeAuthors,
    !!includeBioSampleContainer,
    restOptions,
  );

  const bioSampleContainer = bioSampleContainers[0];

  const { aliquotParent, aliquotChildren } = await fetchAliquots(
    {
      bioSample,
      study,
      studyConfig,
      includeAliquotParent,
      includeAliquotChildren,
    },
    restOptions,
  );

  const bioSampleItem = toBioSampleItem(
    bioSample,
    bioSampleContainer,
    aliquotParent,
    aliquotChildren,
    bookedVisits,
    patients,
    authors,
  );

  if (bioSampleItem) {
    return bioSampleItem;
  } else {
    throw CspError.notFound(`BioSampleItem with id ${sampleId} not found`);
  }
};

const queryBySampleCode = async (
  {
    sampleCode,
    study,
    studyConfig,
    includeAuthors,
    includeAliquotParent,
    includeAliquotChildren,
    includeBioSampleContainer,
  }: FetchBioSampleItemBySampleCodeIn,
  apiOptions?: ApiOptions,
): Promise<Maybe<BioSampleItem>> => {
  const restOptions = toRestOptions(apiOptions);
  const bioSampleQuery = await query(
    {
      study,
      studyConfig,
      bioSampleQueryIn: BioSampleQueryMapper.fromQuery({
        pageSize: 1,
        criteria: {
          sampleCodes: [sampleCode],
        },
      }),
      includeBioSampleContainer,
      includeAuthors,
    },
    restOptions,
  );

  const bioSampleItem = bioSampleQuery.data[0];

  if (bioSampleItem) {
    const { aliquotParent, aliquotChildren } = await fetchAliquots(
      {
        bioSample: bioSampleItem,
        study,
        studyConfig,
        includeAliquotParent,
        includeAliquotChildren,
      },
      restOptions,
    );

    return {
      ...bioSampleItem,
      aliquotParent,
      aliquotChildren,
    };
  } else {
    return undefined;
  }
};

export const BioSampleItemQueryService = {
  query,
  fetchById,
  queryBySampleCode,
  fetchAuthorsForSample,
};
